/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Reducer for Labels Import
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.3.0
 */

import produce from 'immer';
import { Reducer } from 'redux';

import { EMimeType } from '../../modules/app/appConstants';
import { WIZARD_STORAGE_CLEAR_STEPS } from '../../modules/wizard/wizardConstants';
import { TLabelsImportActions } from './labelsImportActions';
import {
    ELabelsImportLabelsValidationStatus,
    ELabelsImportSteps,
    LABELS_IMPORT_CLEAR_WIZARD_DATA,
    LABELS_IMPORT_LOAD_LABELS_VALIDATION_FAILURE,
    LABELS_IMPORT_SET_FILE_ERROR,
    LABELS_IMPORT_SET_FILE_META,
    LABELS_IMPORT_SET_FILE_OBJECTS,
    LABELS_IMPORT_SET_FILE_UPLOADED,
    LABELS_IMPORT_SET_IS_STEP_VALID,
    LABELS_IMPORT_SET_LABELS_VALIDATION_INFO,
    LABELS_IMPORT_SET_SAVED_PARAMS,
    LABELS_IMPORT_SET_TOTAL_ROWS_COUNT,
} from './labelsImportConstants';

export interface ILabelsImportFileMeta {
    maxFileSize: number;
    maxLabelLength: number;
    maxLabelsPerSubs: number;
    maxRows: number;
    templateUrl: string;
    templateFormats: string[];
}

export interface ILabelsImportFileInfoItem {
    name: string;
    mimeType: EMimeType;
}

export interface ILabelsImportLabelsValidationInfo {
    maxLabelsCount: number;
    subscriberCount: number;
    successRowsCount: number;
    status: ELabelsImportLabelsValidationStatus;
}

export interface ILabelsImportReducer {
    [ELabelsImportSteps.File]: ILabelsImportFileMeta;
    fileError: string;
    fileInfoItems: ILabelsImportFileInfoItem[];
    fileObjects: File[];
    isDataRestoredFromStorage: boolean;
    isFileUploaded: boolean;
    isMaxRowsExceeded: boolean;
    isStepValid: boolean;
    labelsValidationInfo: ILabelsImportLabelsValidationInfo;
    labelsValidationError: string;
    totalRowsCount: number;
    validateError: string;
}

export const initialLabelsImportFileMetaState: ILabelsImportFileMeta = {
    maxLabelLength: 0,
    maxLabelsPerSubs: 0,
    maxRows: 0,
    maxFileSize: 0,
    templateUrl: '',
    templateFormats: [],
}

export const initialLabelsImportLabelsValidationInfo: ILabelsImportLabelsValidationInfo = {
    maxLabelsCount: 0,
    subscriberCount: 0,
    successRowsCount: 0,
    status: ELabelsImportLabelsValidationStatus.UNDEFINED,
}

export const initialLabelsImportState: ILabelsImportReducer = {
    [ELabelsImportSteps.File]: initialLabelsImportFileMetaState,
    fileError: '',
    fileInfoItems: [],
    fileObjects: [],
    isDataRestoredFromStorage: false,
    isFileUploaded: false,
    isMaxRowsExceeded: false,
    isStepValid: false,
    labelsValidationInfo: initialLabelsImportLabelsValidationInfo,
    labelsValidationError: '',
    totalRowsCount: 0,
    validateError: '',
};

export const labelsImportReducer: Reducer<ILabelsImportReducer, TLabelsImportActions> =
    (state = initialLabelsImportState, action: TLabelsImportActions) =>
        produce(state, draft => {
            switch (action.type) {
                case LABELS_IMPORT_SET_FILE_ERROR:
                    draft.fileError = action.payload.error;
                    break;
                case LABELS_IMPORT_SET_FILE_OBJECTS:
                    draft.fileObjects = action.payload.fileObjects;
                    draft.fileInfoItems = action.payload.fileObjects.map(file => ({
                        name: file.name,
                        mimeType: file.type as EMimeType,
                    }));
                    draft.isFileUploaded = false;
                    draft.fileError = initialLabelsImportState.fileError;
                    break;
                case LABELS_IMPORT_SET_FILE_META:
                    draft[ELabelsImportSteps.File] = {
                        ...action.payload,
                        templateFormats: action.payload.templateFormats.map(format => format.toLowerCase()),
                    };
                    break;
                case LABELS_IMPORT_SET_LABELS_VALIDATION_INFO:
                    draft.labelsValidationInfo = action.payload.labelsValidationInfo;
                    break;
                case LABELS_IMPORT_SET_FILE_UPLOADED:
                    draft.isFileUploaded = action.payload.isFileUploaded;
                    draft.isMaxRowsExceeded = action.payload.isMaxRowsExceeded;
                    draft.totalRowsCount = action.payload.totalRowsCount;
                    draft.labelsValidationInfo = initialLabelsImportLabelsValidationInfo;
                    break;
                case LABELS_IMPORT_SET_IS_STEP_VALID:
                    draft.isStepValid = action.payload.isStepValid;
                    draft.validateError = action.payload.validateError;
                    break;
                case LABELS_IMPORT_CLEAR_WIZARD_DATA:
                    return initialLabelsImportState;
                case LABELS_IMPORT_LOAD_LABELS_VALIDATION_FAILURE:
                    draft.labelsValidationError = action.payload.errorMessage;
                    break;
                case LABELS_IMPORT_SET_SAVED_PARAMS:
                    draft.fileInfoItems = action.payload.fileInfoItems;
                    draft.isFileUploaded = action.payload.isFileUploaded;
                    draft.isMaxRowsExceeded = action.payload.isMaxRowsExceeded;
                    draft.totalRowsCount = action.payload.totalRowsCount;
                    draft[ELabelsImportSteps.File] = action.payload[ELabelsImportSteps.File];

                    draft.isDataRestoredFromStorage = true;
                    break;
                case LABELS_IMPORT_SET_TOTAL_ROWS_COUNT:
                    draft.totalRowsCount = action.payload.totalRowsCount;
                    break;

                case WIZARD_STORAGE_CLEAR_STEPS:
                    Object.assign(draft, initialLabelsImportState);
                    break;

                default:
                    break;
            }
        })
